import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Check, ExternalLink, Loader2, X } from "lucide-react";
import { updateCreatorStatus } from "@/lib/admin-audit.functions";
import { resolveImage } from "@/lib/image-map";
import { supabase } from "@/integrations/supabase/client";
import { VerifiedBadge } from "@/components/verified-badge";

type CreatorRow = {
  id: string;
  handle: string;
  name: string;
  bio: string | null;
  avatar_url: string | null;
  followers: string | null;
  status: "pending" | "approved" | "live" | "rejected";
  categories: string[] | null;
  pitch: string | null;
  instagram: string | null;
  tiktok: string | null;
  website: string | null;
  rejection_note: string | null;
  created_at: string;
  is_verified?: boolean;
};

type TabKey = CreatorRow["status"] | "all";
const TABS: { key: TabKey; label: string }[] = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "live", label: "Live" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

const PAGE_SIZE = 20;

export const Route = createFileRoute("/_authenticated/admin/creators/")({
  component: AdminCreatorsIndex,
});

function AdminCreatorsIndex() {
  const [tab, setTab] = useState<TabKey>("pending");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const counts = useQuery({
    queryKey: ["admin-creator-counts"],
    queryFn: async () => {
      const statuses: CreatorRow["status"][] = ["pending", "approved", "live", "rejected"];
      const results = await Promise.all(
        statuses.map(async (s) => {
          const { count, error } = await supabase
            .from("creators")
            .select("id", { count: "exact", head: true })
            .eq("status", s);
          if (error) throw error;
          return [s, count ?? 0] as const;
        }),
      );
      const map = Object.fromEntries(results) as Record<CreatorRow["status"], number>;
      return { ...map, all: map.pending + map.approved + map.live + map.rejected };
    },
  });

  const list = useInfiniteQuery({
    queryKey: ["admin-creators", tab, debouncedSearch],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const from = (pageParam as number) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      let q = supabase
        .from("creators")
        .select("*")
        .order("created_at", { ascending: false })
        .range(from, to);
      if (tab !== "all") q = q.eq("status", tab);
      if (debouncedSearch) {
        const term = `%${debouncedSearch}%`;
        q = q.or(`handle.ilike.${term},name.ilike.${term}`);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as CreatorRow[];
    },
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < PAGE_SIZE ? undefined : allPages.length,
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && list.hasNextPage && !list.isFetchingNextPage) {
          list.fetchNextPage();
        }
      },
      { rootMargin: "400px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [list.hasNextPage, list.isFetchingNextPage, list.fetchNextPage]);

  const rows = list.data?.pages.flat() ?? [];
  const totalForTab = counts.data?.[tab];

  return (
    <div>
      <h2 className="font-serif text-2xl">Creator review</h2>
      <p className="mt-1 text-sm text-muted-foreground">Approve, take live, or reject applicants with an optional note.</p>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by handle or name…"
            className="w-full border border-border bg-card/80 px-4 py-2.5 text-sm outline-none transition-colors focus:border-foreground"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground"
            >
              Clear
            </button>
          )}
        </div>
        <select
          value={tab}
          onChange={(e) => setTab(e.target.value as TabKey)}
          className="border border-border bg-card/80 px-4 py-2.5 text-sm uppercase tracking-widest outline-none transition-colors focus:border-foreground"
        >
          {TABS.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      <p className="mt-2 text-xs uppercase tracking-widest text-muted-foreground">
        {counts.data?.[tab] != null ? `${counts.data[tab]} creators` : "Loading…"}
      </p>

      <div className="mt-8 space-y-4">
        {/* Desktop */}
        <div className="hidden md:block space-y-4">
          {list.isLoading && <p className="text-sm text-muted-foreground">Loading applications…</p>}
          {list.error && <p className="text-sm text-destructive">{(list.error as Error).message}</p>}
          {!list.isLoading && rows.length === 0 && (
            <p className="text-sm text-muted-foreground">No applications in this view.</p>
          )}
          {rows.map((c) => (
            <ApplicationCard key={c.id} creator={c} />
          ))}
        </div>

        {/* Mobile */}
        <div className="block md:hidden space-y-2">
          {list.isLoading && <p className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</p>}
          {list.error && <p className="px-4 py-6 text-center text-sm text-destructive">{(list.error as Error).message}</p>}
          {!list.isLoading && rows.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">No applications in this view.</p>
          )}
          {rows.map((c) => (
            <MobileApplicationCard key={c.id} creator={c} />
          ))}
        </div>

        <div ref={sentinelRef} className="h-1" />

        {list.isFetchingNextPage && (
          <p className="flex items-center justify-center gap-2 py-6 text-xs uppercase tracking-widest text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading more
          </p>
        )}
        {!list.hasNextPage && rows.length > 0 && (
          <p className="py-6 text-center text-xs uppercase tracking-widest text-muted-foreground">
            End of list · {rows.length}{totalForTab != null ? ` of ${totalForTab}` : ""}
          </p>
        )}
      </div>
    </div>
  );
}

const statusTone: Record<CreatorRow["status"], string> = {
  pending: "bg-amber-100 text-amber-900",
  approved: "bg-emerald-100 text-emerald-900",
  live: "bg-primary text-primary-foreground",
  rejected: "bg-rose-100 text-rose-900",
};

function ApplicationCard({ creator }: { creator: CreatorRow }) {
  const qc = useQueryClient();
  const [showReject, setShowReject] = useState(false);
  const [note, setNote] = useState(creator.rejection_note ?? "");
  const updateStatus = useServerFn(updateCreatorStatus);

  const mutate = useMutation({
    mutationFn: async (patch: { status: CreatorRow["status"]; rejection_note?: string | null }) => {
      await updateStatus({
        data: {
          creatorId: creator.id,
          status: patch.status,
          rejectionNote: patch.rejection_note ?? null,
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-creators"] });
      qc.invalidateQueries({ queryKey: ["admin-creator-counts"] });
      qc.invalidateQueries({ queryKey: ["creators"] });
      qc.invalidateQueries({ queryKey: ["admin-audit-log"] });
      setShowReject(false);
    },
  });

  useEffect(() => {
    if (!showReject) return;
    const t = setTimeout(() => setShowReject(false), 5000);
    return () => clearTimeout(t);
  }, [showReject]);

  return (
    <div className="border border-border bg-card">
      <div className="flex items-start gap-4 p-5">
        <img src={resolveImage(creator.avatar_url)} alt={creator.name} className="h-14 w-14 rounded-full object-cover" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-serif text-lg inline-flex items-center gap-1.5">
              {creator.name}
              {creator.is_verified && <VerifiedBadge />}
            </p>
            <span className="text-xs text-muted-foreground">@{creator.handle}</span>
            <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${statusTone[creator.status]}`}>{creator.status}</span>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">{creator.bio || "No bio provided."}</p>

          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {creator.followers && <span>{creator.followers} followers</span>}
            {(creator.categories ?? []).length > 0 && <span>Categories: {creator.categories!.join(", ")}</span>}
            {creator.instagram && <span>IG: {creator.instagram}</span>}
            {creator.tiktok && <span>TT: {creator.tiktok}</span>}
            {creator.website && (
              <a href={creator.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">
                Site <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>

          {creator.pitch && (
            <p className="mt-3 border-l-2 border-border pl-3 text-sm italic text-foreground/80">&quot;{creator.pitch}&quot;</p>
          )}

          {creator.status === "rejected" && creator.rejection_note && (
            <p className="mt-3 text-xs text-muted-foreground">Note to applicant: {creator.rejection_note}</p>
          )}
        </div>

        <div className="flex shrink-0 flex-col gap-2">
          {showReject ? (
            <>
              <button
                onClick={() => mutate.mutate({ status: "rejected", rejection_note: note.trim() || null })}
                disabled={mutate.isPending}
                className="bg-destructive px-3 py-2 text-xs uppercase tracking-widest text-destructive-foreground disabled:opacity-50"
              >
                {mutate.isPending ? "Saving…" : "Confirm reject"}
              </button>
              <button onClick={() => setShowReject(false)} className="border border-border px-3 py-2 text-xs uppercase tracking-widest">
                Cancel
              </button>
            </>
          ) : (
            <>
              <Link
                to="/admin/creators/$id"
                params={{ id: creator.id }}
                className="border border-border px-3 py-2 text-center text-xs uppercase tracking-widest hover:bg-foreground hover:text-background"
              >
                View detail
              </Link>
              {creator.status !== "approved" && creator.status !== "live" && (
                <button
                  onClick={() => mutate.mutate({ status: "approved", rejection_note: null })}
                  disabled={mutate.isPending}
                  className="inline-flex items-center gap-1 border border-border px-3 py-2 text-xs uppercase tracking-widest hover:bg-foreground hover:text-background disabled:opacity-50"
                >
                  <Check className="h-3 w-3" /> Approve
                </button>
              )}
              {creator.status === "approved" && (
                <button
                  onClick={() => mutate.mutate({ status: "live" })}
                  disabled={mutate.isPending}
                  className="border border-border px-3 py-2 text-xs uppercase tracking-widest hover:bg-foreground hover:text-background disabled:opacity-50"
                >
                  Take live
                </button>
              )}
              {creator.status === "live" && (
                <Link
                  to="/creator/$handle"
                  params={{ handle: creator.handle }}
                  className="border border-border px-3 py-2 text-center text-xs uppercase tracking-widest hover:bg-foreground hover:text-background"
                >
                  View
                </Link>
              )}
              {creator.status !== "rejected" && (
                <button
                  onClick={() => setShowReject(true)}
                  className="inline-flex items-center gap-1 border border-border px-3 py-2 text-xs uppercase tracking-widest hover:bg-destructive hover:text-destructive-foreground"
                >
                  <X className="h-3 w-3" /> Reject
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {showReject && (
        <div className="border-t border-border bg-background p-5">
          <label className="block text-xs uppercase tracking-widest text-muted-foreground">Rejection note (optional)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Shared with the applicant. Keep it kind and specific."
            className="mt-2 w-full border border-border bg-card px-3 py-2 text-sm outline-none focus:border-foreground"
          />
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => mutate.mutate({ status: "rejected", rejection_note: note.trim() || null })}
              disabled={mutate.isPending}
              className="bg-destructive px-4 py-2 text-xs uppercase tracking-widest text-destructive-foreground disabled:opacity-50"
            >
              {mutate.isPending ? "Saving…" : "Confirm reject"}
            </button>
            <button onClick={() => setShowReject(false)} className="border border-border px-4 py-2 text-xs uppercase tracking-widest">
              Cancel
            </button>
          </div>
          {mutate.error && <p className="mt-2 text-xs text-destructive">{(mutate.error as Error).message}</p>}
        </div>
      )}
    </div>
  );
}

function MobileApplicationCard({ creator }: { creator: CreatorRow }) {
  const qc = useQueryClient();
  const [showReject, setShowReject] = useState(false);
  const [note, setNote] = useState(creator.rejection_note ?? "");
  const updateStatus = useServerFn(updateCreatorStatus);

  const mutate = useMutation({
    mutationFn: async (patch: { status: CreatorRow["status"]; rejection_note?: string | null }) => {
      await updateStatus({
        data: {
          creatorId: creator.id,
          status: patch.status,
          rejectionNote: patch.rejection_note ?? null,
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-creators"] });
      qc.invalidateQueries({ queryKey: ["admin-creator-counts"] });
      qc.invalidateQueries({ queryKey: ["creators"] });
      qc.invalidateQueries({ queryKey: ["admin-audit-log"] });
      setShowReject(false);
    },
  });

  useEffect(() => {
    if (!showReject) return;
    const t = setTimeout(() => setShowReject(false), 5000);
    return () => clearTimeout(t);
  }, [showReject]);

  return (
    <div className="border border-border bg-card p-4 rounded space-y-2">
      <div className="flex items-center gap-3">
        <img src={resolveImage(creator.avatar_url)} alt={creator.name} className="h-10 w-10 rounded-full object-cover shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate inline-flex items-center gap-1.5">
            {creator.name}
            {creator.is_verified && <VerifiedBadge />}
          </p>
          <p className="text-xs text-muted-foreground">@{creator.handle}</p>
        </div>
        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${statusTone[creator.status]}`}>{creator.status}</span>
      </div>
      {creator.followers && <p className="text-xs text-muted-foreground">{creator.followers} followers</p>}
      <div className="flex flex-wrap gap-1 pt-1">
        {showReject ? (
          <>
            <button
              onClick={() => mutate.mutate({ status: "rejected", rejection_note: note.trim() || null })}
              disabled={mutate.isPending}
              className="bg-destructive px-3 py-1.5 text-[10px] uppercase tracking-widest text-destructive-foreground disabled:opacity-50"
            >
              {mutate.isPending ? "Saving…" : "Confirm reject"}
            </button>
            <button onClick={() => setShowReject(false)} className="border border-border px-3 py-1.5 text-[10px] uppercase tracking-widest">
              Cancel
            </button>
          </>
        ) : (
          <>
            <Link
              to="/admin/creators/$id"
              params={{ id: creator.id }}
              className="border border-border px-2 py-1 text-center text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background"
            >
              View
            </Link>
            {creator.status !== "approved" && creator.status !== "live" && (
              <button
                onClick={() => mutate.mutate({ status: "approved", rejection_note: null })}
                disabled={mutate.isPending}
                className="inline-flex items-center gap-1 border border-border px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background disabled:opacity-50"
              >
                <Check className="h-3 w-3" /> Approve
              </button>
            )}
            {creator.status === "approved" && (
              <button
                onClick={() => mutate.mutate({ status: "live" })}
                disabled={mutate.isPending}
                className="border border-border px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background disabled:opacity-50"
              >
                Take live
              </button>
            )}
            {creator.status === "live" && (
              <Link
                to="/creator/$handle"
                params={{ handle: creator.handle }}
                className="border border-border px-2 py-1 text-center text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background"
              >
                View
              </Link>
            )}
            {creator.status !== "rejected" && (
              <button
                onClick={() => setShowReject(true)}
                className="inline-flex items-center gap-1 border border-border px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-destructive hover:text-destructive-foreground"
              >
                <X className="h-3 w-3" /> Reject
              </button>
            )}
          </>
        )}
      </div>
      {showReject && (
        <div className="space-y-2 pt-2">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="Rejection note (optional)"
            className="w-full border border-border bg-card px-3 py-2 text-sm outline-none focus:border-foreground"
          />
          <div className="flex gap-2">
            <button
              onClick={() => mutate.mutate({ status: "rejected", rejection_note: note.trim() || null })}
              disabled={mutate.isPending}
              className="bg-destructive px-3 py-1.5 text-[10px] uppercase tracking-widest text-destructive-foreground disabled:opacity-50"
            >
              {mutate.isPending ? "Saving…" : "Confirm"}
            </button>
            <button onClick={() => setShowReject(false)} className="border border-border px-3 py-1.5 text-[10px] uppercase tracking-widest">
              Cancel
            </button>
          </div>
          {mutate.error && <p className="text-xs text-destructive">{(mutate.error as Error).message}</p>}
        </div>
      )}
    </div>
  );
}